/**
 * Strategy Code Generator
 * Converts no-code strategy configuration into CandleScript code.
 * Also provides utilities to parse code back into inputs/styles.
 *
 * IMPORTANT: Generated code uses explicit for-loops for element-wise
 * comparisons because the sandbox runs plain JS where Float64Array
 * operators (>, <, &&) do NOT work element-wise.
 */

/**
 * All available visual builder condition types
 */
export const SYSTEM_INDICATORS = [
    { type: 'SMA', label: 'Simple Moving Average', category: 'Moving Average', params: { period: 50 } },
    { type: 'EMA', label: 'Exponential Moving Average', category: 'Moving Average', params: { period: 21 } },
    { type: 'WMA', label: 'Weighted Moving Average', category: 'Moving Average', params: { period: 20 } },
    { type: 'HMA', label: 'Hull Moving Average', category: 'Moving Average', params: { period: 20 } },
    { type: 'DEMA', label: 'Double EMA', category: 'Moving Average', params: { period: 20 } },
    { type: 'TEMA', label: 'Triple EMA', category: 'Moving Average', params: { period: 20 } },
    { type: 'RSI', label: 'Relative Strength Index', category: 'Momentum', params: { period: 14, level: 50 } },
    { type: 'MOM', label: 'Momentum', category: 'Momentum', params: { period: 10 } },
    { type: 'ROC', label: 'Rate of Change', category: 'Momentum', params: { period: 12 } },
    { type: 'MACD', label: 'MACD', category: 'Momentum', params: { fast: 12, slow: 26, signal: 9 } },
    { type: 'CCI', label: 'Commodity Channel Index', category: 'Momentum', params: { period: 20 } },
    { type: 'ATR', label: 'Average True Range', category: 'Volatility', params: { period: 14 } },
    { type: 'BB', label: 'Bollinger Bands', category: 'Volatility', params: { period: 20, mult: 2 } },
    { type: 'KC', label: 'Keltner Channels', category: 'Volatility', params: { period: 20, mult: 1.5 } },
    { type: 'Volume', label: 'Volume Average', category: 'Volume', params: { period: 20 } },
    { type: 'OBV', label: 'On Balance Volume', category: 'Volume', params: {} },
    { type: 'VWAP', label: 'VWAP', category: 'Volume', params: {} },
    { type: 'DC', label: 'Donchian Channel', category: 'Channels', params: { period: 20 } },
    { type: 'SuperTrend', label: 'SuperTrend', category: 'Trend', params: { period: 10, mult: 3 } },
    { type: 'SAR', label: 'Parabolic SAR', category: 'Trend', params: {} },
    { type: 'Ichimoku', label: 'Ichimoku', category: 'Trend', params: {} },
    { type: 'PivotPoints', label: 'Pivot Points', category: 'Levels', params: {} },
    { type: 'LinearRegressionChannel', label: 'Linear Regression Channel', category: 'Channels', params: { period: 100 } },
    { type: 'VisualTrading', label: 'VT Target', category: 'Visual Trading', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3 } },
    { type: 'VTLaser', label: 'VT Laser Target', category: 'Visual Trading', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3 } },
];

export const ENTRY_CONDITION_TYPES = [
    { type: 'breakout', label: 'Breakout of High', category: 'price', params: { period: 20 } },
    { type: 'breakdown', label: 'Breakdown of Low', category: 'price', params: { period: 20 } },
    { type: 'volume_spike', label: 'Volume Spike', category: 'volume', params: { multiplier: 1.5 } },
    { type: 'ma_crossover', label: 'MA Crossover (Fast/Slow)', category: 'trend', params: { fastPeriod: 9, slowPeriod: 21, maType: 'EMA' } },
    { type: 'ma_crossunder', label: 'MA Crossunder (Fast/Slow)', category: 'trend', params: { fastPeriod: 9, slowPeriod: 21, maType: 'EMA' } },
    { type: 'price_above_ma', label: 'Price Above MA', category: 'trend', params: { period: 50, maType: 'SMA' } },
    { type: 'price_below_ma', label: 'Price Below MA', category: 'trend', params: { period: 50, maType: 'SMA' } },
    { type: 'rsi_oversold', label: 'RSI Oversold', category: 'momentum', params: { period: 14, level: 30 } },
    { type: 'rsi_overbought', label: 'RSI Overbought', category: 'momentum', params: { period: 14, level: 70 } },
    { type: 'rsi_crossover', label: 'RSI Cross Above Level', category: 'momentum', params: { period: 14, level: 50 } },
    { type: 'rsi_crossunder', label: 'RSI Cross Below Level', category: 'momentum', params: { period: 14, level: 50 } },
    { type: 'macd_crossover', label: 'MACD Bullish Cross', category: 'momentum', params: { fast: 12, slow: 26, signal: 9 } },
    { type: 'macd_crossunder', label: 'MACD Bearish Cross', category: 'momentum', params: { fast: 12, slow: 26, signal: 9 } },
    { type: 'macd_above_zero', label: 'MACD Above Zero', category: 'momentum', params: { fast: 12, slow: 26, signal: 9 } },
    { type: 'macd_below_zero', label: 'MACD Below Zero', category: 'momentum', params: { fast: 12, slow: 26, signal: 9 } },
    { type: 'bb_reentry_long', label: 'Bollinger Lower Band Recovery', category: 'range', params: { period: 20, mult: 2 } },
    { type: 'bb_reentry_short', label: 'Bollinger Upper Band Rejection', category: 'range', params: { period: 20, mult: 2 } },
    { type: 'adx_trending', label: 'ADX Trending Regime', category: 'regime', params: { period: 14, threshold: 25 } },
    { type: 'adx_ranging', label: 'ADX Range Regime', category: 'regime', params: { period: 14, threshold: 25 } },
    { type: 'bb_squeeze', label: 'Bollinger Band Squeeze', category: 'volatility', params: { period: 20, mult: 2, threshold: 0.5 } },
    { type: 'bb_breakout_upper', label: 'BB Breakout Upper', category: 'volatility', params: { period: 20, mult: 2 } },
    { type: 'bb_breakout_lower', label: 'BB Breakout Lower', category: 'volatility', params: { period: 20, mult: 2 } },
    { type: 'candle_engulfing_bull', label: 'Bullish Engulfing', category: 'pattern', params: {} },
    { type: 'candle_engulfing_bear', label: 'Bearish Engulfing', category: 'pattern', params: {} },
    { type: 'candle_hammer', label: 'Hammer', category: 'pattern', params: {} },
    { type: 'candle_doji', label: 'Doji', category: 'pattern', params: {} },
    { type: 'higher_high', label: 'Higher High', category: 'structure', params: { lookback: 10 } },
    { type: 'lower_low', label: 'Lower Low', category: 'structure', params: { lookback: 10 } },
    { type: 'inside_bar', label: 'Inside Bar', category: 'pattern', params: {} },
    { type: 'outside_bar', label: 'Outside Bar', category: 'pattern', params: {} },
    { type: 'atr_expansion', label: 'ATR Expansion', category: 'volatility', params: { period: 14, multiplier: 1.5 } },
    { type: 'volume_above_avg', label: 'Volume Above Average', category: 'volume', params: { period: 20, multiplier: 1.0 } },
    { type: 'price_gap_up', label: 'Gap Up', category: 'price', params: { minPercent: 0.5 } },
    { type: 'price_gap_down', label: 'Gap Down', category: 'price', params: { minPercent: 0.5 } },
    { type: 'new_high', label: 'New N-Period High', category: 'price', params: { period: 52 } },
    { type: 'new_low', label: 'New N-Period Low', category: 'price', params: { period: 52 } },
    { type: 'indicator_above', label: 'Indicator Above Level', category: 'indicator', params: { indicator: 'RSI', period: 14, level: 50 } },
    { type: 'indicator_below', label: 'Indicator Below Level', category: 'indicator', params: { indicator: 'RSI', period: 14, level: 50 } },
    { type: 'indicator_cross_above', label: 'Indicator Cross Above Level', category: 'indicator', params: { indicator: 'RSI', period: 14, level: 50 } },
    { type: 'indicator_cross_below', label: 'Indicator Cross Below Level', category: 'indicator', params: { indicator: 'RSI', period: 14, level: 50 } },
    { type: 'vt_target_buy', label: 'VT Target Buy Setup', category: 'visual trading', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3 } },
    { type: 'vt_target_sell', label: 'VT Target Sell Setup', category: 'visual trading', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3 } },
    { type: 'vt_laser_buy', label: 'VT Laser Buy Setup', category: 'visual trading', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3 } },
    { type: 'vt_laser_sell', label: 'VT Laser Sell Setup', category: 'visual trading', params: { smaLength: 34, dcLength: 10, riskRewardRatio: 1.5 } },
    { type: 'vt_with_trend_confirmation', label: 'VT + Trend Confirmation', category: 'visual trading', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3, confirmTrendPeriod: 50, requireTrendDirection: 'above' } },
    { type: 'vt_with_momentum_confirmation', label: 'VT + Momentum Confirmation', category: 'visual trading', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3, rsiPeriod: 14, rsiMinBull: 55, rsiMaxBear: 45 } },
    { type: 'vt_with_volume_confirmation', label: 'VT + Volume Confirmation', category: 'visual trading', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3, volumePeriod: 20, volumeMultiplier: 1.2 } },
    { type: 'confidence_gate', label: 'Confidence Score Gate', category: 'risk quality', params: { minConfidence: 70, fastTrend: 21, slowTrend: 50, rsiPeriod: 14, rsiBull: 55, rsiBear: 45, volumeMultiplier: 1.0 } },
];

export const EXIT_CONDITION_TYPES = [
    { type: 'atr', label: 'ATR-based Stop', params: { period: 14, multiplier: 1.5 } },
    { type: 'percent', label: 'Percentage Stop', params: { percent: 2 } },
    { type: 'fixed_points', label: 'Fixed Points', params: { points: 50 } },
    { type: 'trailing_atr', label: 'Trailing ATR Stop', params: { period: 14, multiplier: 2 } },
    { type: 'trailing_percent', label: 'Trailing Percent', params: { percent: 1.5 } },
    { type: 'ma_cross_exit', label: 'MA Cross Exit', params: { fastPeriod: 9, slowPeriod: 21 } },
    { type: 'rsi_exit', label: 'RSI Exit Level', params: { period: 14, exitLevel: 70 } },
    { type: 'time_based', label: 'Time-based Exit', params: { bars: 20 } },
    { type: 'rrr', label: 'Risk-Reward Ratio', params: { ratio: 2.0 } },
    { type: 'breakeven', label: 'Move to Breakeven', params: { triggerRR: 1.0 } },
];

export const FILTER_TYPES = [
    { type: 'SMA', label: 'Simple Moving Average', params: { period: 50 } },
    { type: 'EMA', label: 'Exponential Moving Average', params: { period: 50 } },
    { type: 'ATR', label: 'Average True Range', params: { period: 14 } },
    { type: 'Volume', label: 'Volume Filter', params: { period: 20 } },
    { type: 'RSI', label: 'RSI Range Filter', params: { period: 14, min: 30, max: 70 } },
    { type: 'ADX', label: 'ADX Trend Strength', params: { period: 14, minStrength: 25 } },
    { type: 'BB_Width', label: 'Bollinger Width', params: { period: 20, mult: 2 } },
    { type: 'Time', label: 'Time of Day Filter', params: { startHour: 9, endHour: 15 } },
];

/**
 * Generate CandleScript code from strategy configuration
 * @param {Object} config - Strategy configuration from the builder panel
 * @returns {string} Generated CandleScript code
 */
export function generateStrategyCode(config) {
    const L = [];
    const { marketContext = {} } = config;
    const direction = (config.riskSettings && config.riskSettings.tradeDirection) || 'long';
    const conditionOperator = config.conditionOperator === 'OR' ? ' || ' : ' && ';
    L.push('function indicator(input, options) {');
    L.push('    const { open, high, low, close, volume, timestamp } = input;');
    L.push('    const n = close.length;');
    L.push('');
    L.push('    // === Indicators ===');
    const marketFilters = buildMarketFilters(marketContext);
    for (const line of marketFilters.lines) L.push(line);
    for (const indicator of (config.indicators || [])) {
        for (const line of indicatorDeclaration(safeId(indicator.key || indicator.type), indicator)) L.push(line);
    }

    const pre = [];
    const allPre = pre;
    const vtAliases = new Map();
    const checks = [];
    const shortChecks = [];
    const neutralChecks = [];
    const extraPlots = new Set();
    for (const [ruleIndex, c] of (config.entryConditions || []).entries()) {
        const pre = new Set();
        const rulePlots = new Set();
        const shortStart = shortChecks.length;
        const p = c.params || {};
        const checkStart = checks.length;
        switch (c.type) {
            case 'breakout': pre.add(`    const _hi = highest(high, ${p.period||20});`); checks.push('high[i] > _hi[i-1]'); break;
            case 'breakdown': pre.add(`    const _lo = lowest(low, ${p.period||20});`); checks.push('low[i] < _lo[i-1]'); break;
            case 'volume_spike':
                pre.add('    const _entryVolAvg = sma(volume, 20);');
                checks.push(`volume[i] > _entryVolAvg[i] * ${p.multiplier||1.5}`); break;
            case 'ma_crossover':
                pre.add(`    const _mf = ${(p.maType||'ema').toLowerCase()}(close, ${p.fastPeriod||9});`);
                pre.add(`    const _ms = ${(p.maType||'ema').toLowerCase()}(close, ${p.slowPeriod||21});`);
                checks.push('(_mf[i] > _ms[i] && _mf[i-1] <= _ms[i-1])'); break;
            case 'ma_crossunder':
                pre.add(`    const _mfd = ${(p.maType||'ema').toLowerCase()}(close, ${p.fastPeriod||9});`);
                pre.add(`    const _msd = ${(p.maType||'ema').toLowerCase()}(close, ${p.slowPeriod||21});`);
                checks.push('(_mfd[i] < _msd[i] && _mfd[i-1] >= _msd[i-1])'); break;
            case 'price_above_ma': pre.add(`    const _pa = ${(p.maType||'sma').toLowerCase()}(close, ${p.period||50});`); checks.push('close[i] > _pa[i]'); break;
            case 'price_below_ma': pre.add(`    const _pb = ${(p.maType||'sma').toLowerCase()}(close, ${p.period||50});`); checks.push('close[i] < _pb[i]'); break;
            case 'rsi_oversold': pre.add(`    const _rsi = rsi(close, ${p.period||14});`); checks.push(`_rsi[i] < ${p.level||30}`); break;
            case 'rsi_overbought': pre.add(`    const _rsi2 = rsi(close, ${p.period||14});`); checks.push(`_rsi2[i] > ${p.level||70}`); break;
            case 'rsi_crossover': pre.add(`    const _rsiCross = rsi(close, ${p.period||14});`); checks.push(`(_rsiCross[i] > ${p.level||50} && _rsiCross[i-1] <= ${p.level||50})`); break;
            case 'rsi_crossunder': pre.add(`    const _rsiCrossDn = rsi(close, ${p.period||14});`); checks.push(`(_rsiCrossDn[i] < ${p.level||50} && _rsiCrossDn[i-1] >= ${p.level||50})`); break;
            case 'macd_crossover':
                pre.add(`    const _mc = macd(close, ${p.fast||12}, ${p.slow||26}, ${p.signal||9});`);
                checks.push('(_mc.macd[i] > _mc.signal[i] && _mc.macd[i-1] <= _mc.signal[i-1])'); break;
            case 'macd_crossunder':
                pre.add(`    const _mc2 = macd(close, ${p.fast||12}, ${p.slow||26}, ${p.signal||9});`);
                checks.push('(_mc2.macd[i] < _mc2.signal[i] && _mc2.macd[i-1] >= _mc2.signal[i-1])'); break;
            case 'macd_above_zero':
                pre.add(`    const _mcZeroUp = macd(close, ${p.fast||12}, ${p.slow||26}, ${p.signal||9});`);
                checks.push('_mcZeroUp.macd[i] > 0'); break;
            case 'macd_below_zero':
                pre.add(`    const _mcZeroDn = macd(close, ${p.fast||12}, ${p.slow||26}, ${p.signal||9});`);
                checks.push('_mcZeroDn.macd[i] < 0'); break;
            case 'bb_reentry_long':
                pre.add(`    const _bbRecovery = bbands(close, ${p.period||20}, ${p.mult||2});`);
                checks.push('(close[i] > _bbRecovery.lower[i] && close[i-1] <= _bbRecovery.lower[i-1])'); break;
            case 'bb_reentry_short':
                pre.add(`    const _bbRejection = bbands(close, ${p.period||20}, ${p.mult||2});`);
                checks.push('(close[i] < _bbRejection.upper[i] && close[i-1] >= _bbRejection.upper[i-1])'); break;
            case 'adx_trending':
            case 'adx_ranging':
                pre.add(`    const _regimeADX = adx(high, low, close, ${p.period||14});`);
                checks.push(`_regimeADX[i] ${c.type === 'adx_trending' ? '>=' : '<'} ${p.threshold||25}`); break;
            case 'bb_squeeze':
                pre.add(`    const _bbSq = bbands(close, ${p.period||20}, ${p.mult||2});`);
                pre.add(`    const _bbAtr = atr(high, low, close, ${p.period||20});`);
                checks.push(`((_bbSq.upper[i] - _bbSq.lower[i]) / close[i]) < ((_bbAtr[i] / close[i]) * ${p.threshold||0.5})`); break;
            case 'bb_breakout_upper': pre.add(`    const _bb = bbands(close, ${p.period||20}, ${p.mult||2});`); checks.push('close[i] > _bb.upper[i]'); break;
            case 'bb_breakout_lower': pre.add(`    const _bbl = bbands(close, ${p.period||20}, ${p.mult||2});`); checks.push('close[i] < _bbl.lower[i]'); break;
            case 'candle_engulfing_bull':
                checks.push('(close[i] > open[i] && close[i-1] < open[i-1] && close[i] >= open[i-1] && open[i] <= close[i-1])'); break;
            case 'candle_engulfing_bear':
                checks.push('(close[i] < open[i] && close[i-1] > open[i-1] && open[i] >= close[i-1] && close[i] <= open[i-1])'); break;
            case 'candle_hammer':
                checks.push('((Math.min(open[i], close[i]) - low[i]) > Math.abs(close[i] - open[i]) * 2 && (high[i] - Math.max(open[i], close[i])) <= Math.abs(close[i] - open[i]) * 1.2)'); break;
            case 'candle_doji':
                checks.push('(Math.abs(close[i] - open[i]) <= ((high[i] - low[i]) * 0.1))'); break;
            case 'volume_above_avg': pre.add(`    const _va = sma(volume, ${p.period||20});`); checks.push(`volume[i] > _va[i] * ${p.multiplier||1.0}`); break;
            case 'higher_high': pre.add(`    const _hh = highest(high, ${p.lookback||10});`); checks.push('high[i] > _hh[i-1]'); break;
            case 'lower_low': pre.add(`    const _ll = lowest(low, ${p.lookback||10});`); checks.push('low[i] < _ll[i-1]'); break;
            case 'inside_bar':
                checks.push('(high[i] < high[i-1] && low[i] > low[i-1])'); break;
            case 'outside_bar':
                checks.push('(high[i] > high[i-1] && low[i] < low[i-1])'); break;
            case 'atr_expansion':
                pre.add(`    const _atrExp = atr(high, low, close, ${p.period||14});`);
                pre.add(`    const _atrExpAvg = sma(_atrExp, ${p.period||14});`);
                checks.push(`_atrExp[i] > _atrExpAvg[i] * ${p.multiplier||1.5}`); break;
            case 'price_gap_up':
                checks.push(`open[i] > close[i-1] * (1 + ${(p.minPercent||0.5)}/100)`); break;
            case 'price_gap_down':
                checks.push(`open[i] < close[i-1] * (1 - ${(p.minPercent||0.5)}/100)`); break;
            case 'new_high':
                pre.add(`    const _newHi = highest(high, ${p.period||52});`);
                checks.push('high[i] >= _newHi[i]'); break;
            case 'new_low':
                pre.add(`    const _newLo = lowest(low, ${p.period||52});`);
                checks.push('low[i] <= _newLo[i]'); break;
            case 'indicator_above':
            case 'indicator_below':
            case 'indicator_cross_above':
            case 'indicator_cross_below': {
                const id = `_ind_${safeId(p.indicator || 'RSI')}_${checks.length}`;
                for (const line of indicatorDeclaration(id, { type: p.indicator || 'RSI', params: p })) pre.add(line);
                const level = p.level ?? 50;
                if (c.type === 'indicator_above') checks.push(`${id}[i] > ${level}`);
                if (c.type === 'indicator_below') checks.push(`${id}[i] < ${level}`);
                if (c.type === 'indicator_cross_above') checks.push(`(${id}[i] > ${level} && ${id}[i-1] <= ${level})`);
                if (c.type === 'indicator_cross_below') checks.push(`(${id}[i] < ${level} && ${id}[i-1] >= ${level})`);
                rulePlots.add(id);
                break;
            }
            case 'vt_target_buy':
            case 'vt_laser_buy':
                pre.add(`    const _vt = VisualTrading(input, { smaLength: ${p.smaLength||50}, dcLength: ${p.dcLength||20}, riskRewardRatio: ${p.riskRewardRatio || (c.type.includes('laser') ? 1.5 : 3)} });`);
                // Add rising edge detection to VT signals to prevent multiple consecutive triggers
                // Only fire when VT signal first activates: (VT[i] == 1 && VT[i-1] == 0)
                checks.push('(_vt.buy[i] == 1 && _vt.buy[i-1] == 0)');
                shortChecks.push('(_vt.sell[i] == 1 && _vt.sell[i-1] == 0)');
                pre.add('    const _vtSL = _vt.stopLoss;');
                pre.add('    const _vtTP = _vt.takeProfit;');
                rulePlots.add('_vt');
                break;
            case 'vt_target_sell':
            case 'vt_laser_sell':
                pre.add(`    const _vtSell = VisualTrading(input, { smaLength: ${p.smaLength||50}, dcLength: ${p.dcLength||20}, riskRewardRatio: ${p.riskRewardRatio || (c.type.includes('laser') ? 1.5 : 3)} });`);
                // Add rising edge detection to VT signals to prevent multiple consecutive triggers
                // Only fire when VT signal first activates: (VT[i] == 1 && VT[i-1] == 0)
                checks.push('(_vtSell.sell[i] == 1 && _vtSell.sell[i-1] == 0)');
                pre.add('    const _vtSellSL = _vtSell.stopLoss;');
                pre.add('    const _vtSellTP = _vtSell.takeProfit;');
                rulePlots.add('_vtSell');
                break;
            case 'vt_with_trend_confirmation':
                pre.add(`    const _vtConf = VisualTrading(input, { smaLength: ${p.smaLength||50}, dcLength: ${p.dcLength||20}, riskRewardRatio: ${p.riskRewardRatio || (c.type.includes('laser') ? 1.5 : 3)} });`);
                pre.add(`    const _trendConfMA = ema(close, ${p.confirmTrendPeriod||50});`);
                checks.push(`(_vtConf.buy[i] == 1 && _vtConf.buy[i-1] == 0 && close[i] ${p.requireTrendDirection === 'below' ? '<' : '>'} _trendConfMA[i])`);
                shortChecks.push(`(_vtConf.sell[i] == 1 && _vtConf.sell[i-1] == 0 && close[i] ${p.requireTrendDirection === 'above' ? '<' : '>'} _trendConfMA[i])`);
                pre.add('    const _vtConfSL = _vtConf.stopLoss;');
                pre.add('    const _vtConfTP = _vtConf.takeProfit;');
                rulePlots.add('_vtConf');
                break;
            case 'vt_with_momentum_confirmation':
                pre.add(`    const _vtMom = VisualTrading(input, { smaLength: ${p.smaLength||50}, dcLength: ${p.dcLength||20}, riskRewardRatio: ${p.riskRewardRatio || (c.type.includes('laser') ? 1.5 : 3)} });`);
                pre.add(`    const _momRSI = rsi(close, ${p.rsiPeriod||14});`);
                checks.push(`(_vtMom.buy[i] == 1 && _vtMom.buy[i-1] == 0 && _momRSI[i] >= ${p.rsiMinBull||55})`);
                shortChecks.push(`(_vtMom.sell[i] == 1 && _vtMom.sell[i-1] == 0 && _momRSI[i] <= ${p.rsiMaxBear||45})`);
                pre.add('    const _vtMomSL = _vtMom.stopLoss;');
                pre.add('    const _vtMomTP = _vtMom.takeProfit;');
                rulePlots.add('_vtMom');
                break;
            case 'vt_with_volume_confirmation':
                pre.add(`    const _vtVol = VisualTrading(input, { smaLength: ${p.smaLength||50}, dcLength: ${p.dcLength||20}, riskRewardRatio: ${p.riskRewardRatio || (c.type.includes('laser') ? 1.5 : 3)} });`);
                pre.add(`    const _volAvgConf = sma(volume, ${p.volumePeriod||20});`);
                checks.push(`(_vtVol.buy[i] == 1 && _vtVol.buy[i-1] == 0 && volume[i] > _volAvgConf[i] * ${p.volumeMultiplier||1.2})`);
                shortChecks.push(`(_vtVol.sell[i] == 1 && _vtVol.sell[i-1] == 0 && volume[i] > _volAvgConf[i] * ${p.volumeMultiplier||1.2})`);
                pre.add('    const _vtVolSL = _vtVol.stopLoss;');
                pre.add('    const _vtVolTP = _vtVol.takeProfit;');
                rulePlots.add('_vtVol');
                break;
            case 'confidence_gate':
                pre.add(`    const _confFast = ema(close, ${p.fastTrend||21});`);
                pre.add(`    const _confSlow = ema(close, ${p.slowTrend||50});`);
                pre.add(`    const _confRSI = rsi(close, ${p.rsiPeriod||14});`);
                pre.add('    const _confMACD = macd(close, 12, 26, 9);');
                pre.add('    const _confATR = atr(high, low, close, 14);');
                pre.add('    const _confATRAvg = sma(_confATR, 14);');
                pre.add('    const _confVolAvg = sma(volume, 20);');
                pre.add('    const _confidenceLong = new Float64Array(n);');
                pre.add('    const _confidenceShort = new Float64Array(n);');
                pre.add('    for (let j = 1; j < n; j++) {');
                pre.add('        let longScore = 0;');
                pre.add('        let shortScore = 0;');
                pre.add('        if (close[j] > _confSlow[j]) longScore += 20;');
                pre.add('        if (close[j] < _confSlow[j]) shortScore += 20;');
                pre.add('        if (_confFast[j] > _confSlow[j] && _confFast[j] > _confFast[j-1]) longScore += 20;');
                pre.add('        if (_confFast[j] < _confSlow[j] && _confFast[j] < _confFast[j-1]) shortScore += 20;');
                pre.add(`        if (_confRSI[j] >= ${p.rsiBull||55}) longScore += 20;`);
                pre.add(`        if (_confRSI[j] <= ${p.rsiBear||45}) shortScore += 20;`);
                pre.add('        if (_confMACD.macd[j] > _confMACD.signal[j] && _confMACD.macd[j] > 0) longScore += 20;');
                pre.add('        if (_confMACD.macd[j] < _confMACD.signal[j] && _confMACD.macd[j] < 0) shortScore += 20;');
                pre.add(`        if (volume[j] > _confVolAvg[j] * ${p.volumeMultiplier||1.0} && _confATR[j] > _confATRAvg[j]) { longScore += 20; shortScore += 20; }`);
                pre.add('        _confidenceLong[j] = longScore;');
                pre.add('        _confidenceShort[j] = shortScore;');
                pre.add('    }');
                checks.push(`_confidenceLong[i] >= ${p.minConfidence||70}`);
                shortChecks.push(`_confidenceShort[i] >= ${p.minConfidence||70}`);
                rulePlots.add('_confFast');
                rulePlots.add('_confSlow');
                break;
            default: break;
        }
        // Each rule owns its indicator arrays, even when another rule uses
        // the same study with different parameters.
        const scoped = text => text.replace(/\b_[A-Za-z]\w*/g, name => name + '_rule' + ruleIndex);
        for (const line of pre) {
            allPre.push(scoped(line));
            for (const alias of ['_vtSL', '_vtTP', '_vtSellSL', '_vtSellTP']) {
                if (line.includes('const ' + alias + ' =') && !vtAliases.has(alias)) vtAliases.set(alias, scoped(alias));
            }
        }
        for (let i = checkStart; i < checks.length; i++) checks[i] = scoped(checks[i]);
        for (let i = shortStart; i < shortChecks.length; i++) shortChecks[i] = scoped(shortChecks[i]);
        for (const plot of rulePlots) if (!/^_vt/.test(plot)) extraPlots.add(scoped(plot));
        const neutral = ['adx_trending', 'adx_ranging', 'volume_spike', 'volume_above_avg', 'atr_expansion', 'bb_squeeze', 'candle_doji', 'inside_bar', 'outside_bar'];
        const bearish = ['breakdown', 'ma_crossunder', 'price_below_ma', 'rsi_overbought', 'rsi_crossunder', 'macd_crossunder', 'macd_below_zero', 'bb_breakout_lower', 'candle_engulfing_bear', 'lower_low', 'price_gap_down', 'new_low', 'vt_target_sell', 'vt_laser_sell', 'bb_reentry_short'];
        if (neutral.includes(c.type)) neutralChecks.push(...checks.splice(checkStart));
        else if (direction === 'both' && bearish.includes(c.type)) shortChecks.push(...checks.splice(checkStart));
    }
    // Track whether VT SL/TP variables were declared in pre-declarations
    const hasVtSL = [...pre].some(line => line.includes('const _vtSL'));
    const hasVtSellSL = [...pre].some(line => line.includes('const _vtSellSL'));

    for (const p of pre) L.push(p);
    for (const [alias, value] of vtAliases) L.push(`    const ${alias} = ${value};`);
    L.push('');
    L.push('    // === Signal Loop ===');
    L.push('    const buy = new Float64Array(n);');
    L.push('    const sell = new Float64Array(n);');
    L.push('    const slArr = new Float64Array(n);');
    L.push('    const tpArr = new Float64Array(n);');
    L.push('');
    L.push('    for (let i = 1; i < n; i++) {');
    const lp = [...checks, ...neutralChecks];

    if ((direction === 'long' || direction === 'both') && (checks.length || neutralChecks.length))
        L.push(`        if (${lp.length ? '(' + lp.join(conditionOperator) + ')' + (marketFilters.longChecks.length ? ' && (' + marketFilters.longChecks.join(' && ') + ')' : '') : 'false'}) buy[i] = 1;`);
    if (direction === 'short' || direction === 'both') {
        const sp = [...(shortChecks.length ? shortChecks : direction === 'short' ? checks : []), ...neutralChecks];
        const hasEntry = sp.length > 0;

        L.push(`        if (${hasEntry ? '(' + sp.join(conditionOperator) + ')' + (marketFilters.shortChecks.length ? ' && (' + marketFilters.shortChecks.join(' && ') + ')' : '') : 'false'}) sell[i] = 1;`);
    }
    const exitConditions = config.exitConditions || {
        stopLoss: { type: 'percent', params: { percent: 2 } },
        takeProfit: { type: 'rrr', ratio: 2 },
    };

    // Build SL expression — only reference VT SL arrays if they were actually declared
    // Use VT SL/TP only when a VT signal fires at that bar (buy[i] or sell[i] == 1)
    const slVtPart = hasVtSL ? '(buy[i] && _vtSL[i] ? _vtSL[i] : 0) || ' : '';
    const slVtSellPart = hasVtSellSL ? '(sell[i] && _vtSellSL[i] ? _vtSellSL[i] : 0) || ' : '';

    if (exitConditions.stopLoss.type === 'atr') {
        L.splice(L.indexOf('    // === Signal Loop ==='), 0, `    const _stopATR = atr(high, low, close, ${(exitConditions.stopLoss.params && exitConditions.stopLoss.params.period) || 14});`);
        L.push(`        slArr[i] = ${slVtPart}${slVtSellPart}(close[i] - (sell[i] ? -1 : 1) * _stopATR[i] * ${(exitConditions.stopLoss.params && exitConditions.stopLoss.params.multiplier) || exitConditions.stopLoss.multiplier || 1.5});`);
    } else if (exitConditions.stopLoss.type === 'percent') {
        L.push(`        slArr[i] = ${slVtPart}${slVtSellPart}(close[i] * (1 - (sell[i] ? -1 : 1) * ${(exitConditions.stopLoss.params&&exitConditions.stopLoss.params.percent)||2}/100));`);
    } else if (exitConditions.stopLoss.type === 'fixed_points') {
        L.push(`        slArr[i] = close[i] - (sell[i] ? -1 : 1) * ${(exitConditions.stopLoss.params && exitConditions.stopLoss.params.points) || 50};`);
    } else {
        L.push(`        slArr[i] = ${slVtPart}${slVtSellPart}(close[i] * (sell[i] ? 1.02 : 0.98));`);
    }

    const tpRatio = (exitConditions.takeProfit.params && exitConditions.takeProfit.params.ratio) || exitConditions.takeProfit.ratio || 2;
    const tpVtPart = hasVtSL ? '(buy[i] && _vtTP[i] ? _vtTP[i] : 0) || ' : '';
    const tpVtSellPart = hasVtSellSL ? '(sell[i] && _vtSellTP[i] ? _vtSellTP[i] : 0) || ' : '';

    if (exitConditions.takeProfit.type === 'rrr') {
        L.push(`        tpArr[i] = ${tpVtPart}${tpVtSellPart}(close[i] + (close[i] - slArr[i]) * ${tpRatio});`);
    } else if (exitConditions.takeProfit.type === 'percent') {
        L.push(`        tpArr[i] = ${tpVtPart}${tpVtSellPart}(close[i] * (1 + (sell[i] ? -1 : 1) * ${(exitConditions.takeProfit.params&&exitConditions.takeProfit.params.percent)||4}/100));`);
    } else {
        L.push(`        tpArr[i] = ${tpVtPart}${tpVtSellPart}(close[i] + (close[i] - slArr[i]) * 2);`);
    }
    L.push('    }');
    L.push('');
    L.push('    return {');

    // Build plots — skip VT indicator objects (they return { buy, sell, stopLoss, takeProfit }, not arrays)
    const VT_TYPES = new Set(['VisualTrading', 'VTLaser']);
    const plots = [];
    plots.push(...marketFilters.plots);
    for (const indicator of (config.indicators || [])) {
        if (!VT_TYPES.has(indicator.type)) {
            plots.push(safeId(indicator.key || indicator.type));
        }
    }
    // Only add scalar/array extra plots (not VT result objects)
    for (const p of extraPlots) {
        if (p !== '_vt' && p !== '_vtSell') plots.push(p);
    }
    L.push(`        plots: { ${plots.join(', ')} },`);
    L.push('        buy: buy, sell: sell, stopLoss: slArr, takeProfit: tpArr,');
    L.push('    };');
    L.push('}');
    return L.join('\n');
}

function buildMarketFilters(marketContext = {}) {
    const lines = [];
    const longChecks = [];
    const shortChecks = [];
    const plots = [];
    let hasAtrVal = false;

    for (const [key, rawFilter] of Object.entries(marketContext)) {
        const filter = rawFilter || {};
        if (!filter.enabled) continue;

        const id = filterId(key);
        const type = filter.type || 'SMA';
        const params = filter.params || {};
        const period = filter.period || params.period || 14;

        switch (type) {
            case 'EMA':
            case 'SMA': {
                const fn = type === 'EMA' ? 'ema' : 'sma';
                const out = key === 'trendFilter' ? 'trendMA' : `${id}MA`;
                lines.push(`    const ${out} = ${fn}(close, ${period});`);
                const dir = filter.direction || 'Up';
                if (dir === 'Down') {
                    longChecks.push(`${out}[i] < ${out}[i-1]`);
                    shortChecks.push(`${out}[i] > ${out}[i-1]`);
                } else if (dir === 'Price Below') {
                    longChecks.push(`close[i] < ${out}[i]`);
                    shortChecks.push(`close[i] > ${out}[i]`);
                } else if (dir === 'Price Above') {
                    longChecks.push(`close[i] > ${out}[i]`);
                    shortChecks.push(`close[i] < ${out}[i]`);
                } else {
                    longChecks.push(`close[i] > ${out}[i] && ${out}[i] > ${out}[i-1]`);
                    shortChecks.push(`close[i] < ${out}[i] && ${out}[i] < ${out}[i-1]`);
                }
                plots.push(out);
                break;
            }
            case 'ATR': {
                const out = key === 'volatilityFilter' ? 'atrVal' : `${id}ATR`;
                const avg = `${out}Avg`;
                lines.push(`    const ${out} = atr(high, low, close, ${period});`);
                lines.push(`    const ${avg} = sma(${out}, ${period});`);
                hasAtrVal = hasAtrVal || out === 'atrVal';
                longChecks.push(`${out}[i] > ${avg}[i]`);
                shortChecks.push(`${out}[i] > ${avg}[i]`);
                break;
            }
            case 'Volume': {
                const out = key === 'volumeFilter' ? 'volAvg' : `${id}VolAvg`;
                lines.push(`    const ${out} = sma(volume, ${period || 20});`);
                longChecks.push(`volume[i] > ${out}[i]`);
                shortChecks.push(`volume[i] > ${out}[i]`);
                break;
            }
            case 'RSI': {
                const out = `${id}RSI`;
                const min = params.min ?? 30;
                const max = params.max ?? 70;
                lines.push(`    const ${out} = rsi(close, ${period});`);
                longChecks.push(`${out}[i] >= ${min} && ${out}[i] <= ${max}`);
                shortChecks.push(`${out}[i] >= ${min} && ${out}[i] <= ${max}`);
                break;
            }
            case 'BB_Width': {
                const bb = `${id}BB`;
                const width = `${id}Width`;
                const widthAvg = `${id}WidthAvg`;
                lines.push(`    const ${bb} = bbands(close, ${period}, ${params.mult || 2});`);
                lines.push(`    const ${width} = new Float64Array(n);`);
                lines.push(`    for (let j = 0; j < n; j++) ${width}[j] = (${bb}.upper[j] - ${bb}.lower[j]) / close[j];`);
                lines.push(`    const ${widthAvg} = sma(${width}, ${period});`);
                longChecks.push(`${width}[i] > ${widthAvg}[i]`);
                shortChecks.push(`${width}[i] > ${widthAvg}[i]`);
                break;
            }
            case 'ADX': {
                const out = `${id}ADX`;
                lines.push(`    const ${out} = adx(high, low, close, ${period});`);
                longChecks.push(`${out}[i] >= ${params.minStrength || 25}`);
                shortChecks.push(`${out}[i] >= ${params.minStrength || 25}`);
                plots.push(out);
                break;
            }
            case 'Time': {
                const start = Number(params.startHour ?? 9), end = Number(params.endHour ?? 15);
                const hour = '(Math.floor(timestamp[i] / 3600000) % 24)';
                const check = start <= end ? `${hour} >= ${start} && ${hour} < ${end}` : `(${hour} >= ${start} || ${hour} < ${end})`;
                longChecks.push(check); shortChecks.push(check);
                break;
            }
            default:
                break;
        }
    }

    return { lines, longChecks, shortChecks, plots: [...new Set(plots)], hasAtrVal };
}

function filterId(key) {
    return safeId(key.replace(/Filter$/, ''));
}

function safeId(value) {
    return String(value || 'indicator').replace(/[^A-Za-z0-9_$]/g, '_');
}

function indicatorDeclaration(id, indicator) {
    const p = indicator.params || {};
    const type = indicator.type || 'SMA';
    const period = p.period || p.length || 14;
    switch (type) {
        case 'SMA': return [`    const ${id} = sma(close, ${period});`];
        case 'EMA': return [`    const ${id} = ema(close, ${period});`];
        case 'WMA': case 'HMA': case 'DEMA': case 'TEMA': return [`    const ${id} = ${type.toLowerCase()}(close, ${period});`];
        case 'MOM': return [`    const ${id} = momentum(close, ${period});`];
        case 'ROC': return [`    const ${id} = roc(close, ${period});`];
        case 'CCI': return [`    const ${id} = cci(high, low, close, ${period});`];
        case 'OBV': return [`    const ${id} = obv(close, volume);`];
        case 'VWAP': return [`    const ${id} = vwap(high, low, close, volume, timestamp);`];
        case 'KC': return [`    const ${id} = ema(close, ${period});`];
        case 'SuperTrend': return [`    const ${id} = supertrend(high, low, close, ${period}, ${p.mult || 3});`];
        case 'SAR': return [`    const ${id} = sar(high, low);`];
        case 'Ichimoku': return [`    const ${id} = ichimoku(high, low);`];
        case 'PivotPoints': return [`    const ${id} = pivotPoints(high, low, close, timestamp);`];
        case 'LinearRegressionChannel': return [`    const ${id} = linearRegression(close, ${period});`];
        case 'RSI': return [`    const ${id} = rsi(close, ${period});`];
        case 'ATR':
        case 'ATRp': return [`    const ${id} = atr(high, low, close, ${period});`];
        case 'BB': return [`    const _${id}BB = bbands(close, ${p.period || 20}, ${p.mult || 2});`, `    const ${id} = _${id}BB.middle;`];
        case 'MACD': return [`    const _${id}MACD = macd(close, ${p.fast || 12}, ${p.slow || 26}, ${p.signal || 9});`, `    const ${id} = _${id}MACD.macd;`];
        case 'Volume': return [`    const ${id} = sma(volume, ${p.period || 20});`];
        case 'DC': return [`    const ${id} = highest(high, ${p.period || p.dcLength || 20});`];
        case 'VisualTrading':
            // Call the real VisualTrading() function — returns { buy, sell, stopLoss, takeProfit }
            return [
                `    const ${id} = VisualTrading(input, { smaLength: ${p.smaLength || 50}, dcLength: ${p.dcLength || 20}, riskRewardRatio: ${p.riskRewardRatio || 3} });`,
            ];
        case 'VTLaser':
            // VT Laser uses the same VisualTrading engine with tighter params
            return [
                `    const ${id} = VisualTrading(input, { smaLength: ${p.smaLength || 34}, dcLength: ${p.dcLength || 10}, riskRewardRatio: ${p.riskRewardRatio || 1.5} });`,
            ];
        default: return [`    const ${id} = sma(close, ${period}); // ${type} visual proxy`];
    }
}

/**
 * Generate a strategy template based on market intent
 * @param {string} intent - 'intraday' | 'swing' | 'positional'
 * @returns {Object} Default configuration for the intent
 */
export function getIntentDefaults(intent) {
    switch (intent) {
        case 'intraday':
            return {
                trendPeriod: 20,
                atrPeriod: 14,
                volumePeriod: 20,
                slMultiplier: 1.5,
                tpRatio: 2.0,
                riskPercent: 1,
            };
        case 'swing':
            return {
                trendPeriod: 50,
                atrPeriod: 14,
                volumePeriod: 20,
                slMultiplier: 2.0,
                tpRatio: 3.0,
                riskPercent: 2,
            };
        case 'positional':
            return {
                trendPeriod: 200,
                atrPeriod: 20,
                volumePeriod: 50,
                slMultiplier: 3.0,
                tpRatio: 4.0,
                riskPercent: 3,
            };
        default:
            return getIntentDefaults('intraday');
    }
}

/**
 * Parse CandleScript code to extract input parameters
 * Scans for options.xxx patterns and numeric constants used as parameters
 * @param {string} code - The CandleScript code
 * @returns {Array} Array of input definitions
 */
export function parseInputsFromCode(code) {
    const inputs = [];
    const seen = new Set();

    // Match options.xxx or options['xxx'] patterns
    const optionsRegex = /options\.(\w+)\s*\|\|\s*(\d+\.?\d*)/g;
    let match;
    while ((match = optionsRegex.exec(code)) !== null) {
        const key = match[1];
        const defaultVal = parseFloat(match[2]);
        if (!seen.has(key)) {
            seen.add(key);
            const isFloat = match[2].includes('.');
            inputs.push({
                key,
                label: key,
                type: isFloat ? 'float' : 'int',
                default: defaultVal,
                min: isFloat ? 0.1 : 1,
                max: isFloat ? 100 : 500,
                step: isFloat ? 0.1 : 1,
            });
        }
    }

    // Match common TA function calls with numeric period arguments
    const taCallRegex = /\b(sma|ema|rsi|atr|highest|lowest|stddev|bbands|macd)\s*\([^,]+,\s*(\d+)/g;
    const periodNames = { sma: 'smaPeriod', ema: 'emaPeriod', rsi: 'rsiPeriod', atr: 'atrPeriod', highest: 'highPeriod', lowest: 'lowPeriod', stddev: 'stdPeriod', bbands: 'bbPeriod', macd: 'macdFast' };
    while ((match = taCallRegex.exec(code)) !== null) {
        const fn = match[1];
        const period = parseInt(match[2]);
        const key = periodNames[fn] || fn + 'Period';
        if (!seen.has(key)) {
            seen.add(key);
            inputs.push({
                key,
                label: key,
                type: 'int',
                default: period,
                min: 1,
                max: 500,
                step: 1,
            });
        }
    }

    // Match multiplier patterns like * 1.5 or * 2.0
    const multRegex = /\*\s*(\d+\.?\d*)/g;
    let multIdx = 0;
    while ((match = multRegex.exec(code)) !== null) {
        const val = parseFloat(match[1]);
        if (val > 0 && val < 100 && match[1].includes('.')) {
            const key = 'multiplier' + (multIdx > 0 ? multIdx : '');
            if (!seen.has(key)) {
                seen.add(key);
                inputs.push({
                    key,
                    label: key,
                    type: 'float',
                    default: val,
                    min: 0.1,
                    max: 20,
                    step: 0.1,
                });
                multIdx++;
            }
        }
    }

    return inputs;
}

/**
 * Parse CandleScript code to extract plot/style configuration
 * Looks for return statement plots and signal arrays
 * @param {string} code - The CandleScript code
 * @returns {Object} Plot configuration with outputs array
 */
export function parseStylesFromCode(code) {
    const outputs = [];

    // Match plots object keys: plots: { sma50, atr14, ... }
    const plotsMatch = code.match(/plots\s*:\s*\{([^}]+)\}/);
    if (plotsMatch) {
        const plotKeys = plotsMatch[1].split(',').map(s => s.trim().split(':')[0].trim()).filter(Boolean);
        for (const key of plotKeys) {
            outputs.push({
                key,
                label: key,
                plot: 'Line',
                color: getDefaultColor(outputs.length),
                style: 'solid',
                width: 2,
            });
        }
    }

    // Check for buy/sell signal arrays
    if (/\bbuy\b/.test(code) || /\blong\b/.test(code) || /longCondition/.test(code)) {
        outputs.push({ key: 'buy', label: 'Buy Signal', plot: 'Arrow Up', color: '#4CAF50', style: 'solid', width: 2 });
    }
    if (/\bsell\b/.test(code) || /\bshort\b/.test(code) || /shortCondition/.test(code)) {
        outputs.push({ key: 'sell', label: 'Sell Signal', plot: 'Arrow Down', color: '#F44336', style: 'solid', width: 2 });
    }

    // Check for stop loss / take profit
    if (/stopLoss/.test(code)) {
        outputs.push({ key: 'stopLoss', label: 'Stop Loss', plot: 'Line', color: '#F44336', style: 'dashed', width: 1 });
    }
    if (/takeProfit/.test(code)) {
        outputs.push({ key: 'takeProfit', label: 'Take Profit', plot: 'Line', color: '#4CAF50', style: 'dashed', width: 1 });
    }

    return { outputs };
}

/**
 * Get default color for plot index
 */
function getDefaultColor(index) {
    const colors = ['#2962FF', '#FF9800', '#9C27B0', '#00BCD4', '#FF5722', '#8BC34A', '#E91E63', '#3F51B5'];
    return colors[index % colors.length];
}
